import { Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';

import { SecNavButonData, SecondaryNavList } from './models/secondary-nav.models';

interface GetActiveNavItemOptions {
	/**
	 * If true, will ignore the `path` field in the nav item's configuration
	 * @default false
	 */
	ignorePath?: boolean;
}

@Injectable({
	providedIn: 'root'
})
export class SecondaryNavService {
	public readonly hasSecondaryNav = signal(false);
	public readonly secNavHeader = signal('');
	public readonly hasSecondaryNavButton = signal(false);
	public readonly secNavButtonData = signal<SecNavButonData>({
		icon: '',
		buttonText: '',
		showProgressIndicator: false
	});
	public readonly secondaryNavListItems = signal<SecondaryNavList[]>([]);
	private isPermissionAvailable = false;
	private activeListItem$: BehaviorSubject<SecondaryNavList> = new BehaviorSubject<SecondaryNavList>(
		<SecondaryNavList>{}
	);
	public readonly showSecondaryNav = signal(true);

	constructor(private router: Router) {}
	/**
	 * Set secondary Nav list items
	 * @param secondaryNavList[] - array representation of SecondaryNavList list item
	 */
	public setSecondaryNavListItems(secondaryNavList: SecondaryNavList[]): void {
		this.secondaryNavListItems.set(secondaryNavList);
	}

	public addSecondaryNavListItem(itemToAdd: SecondaryNavList): void {
		this.secondaryNavListItems.update((currentItems) => {
			// if item already exists, don't add again.
			const exists = currentItems.find((item) => item.id === itemToAdd.id);
			if (exists) return currentItems;
			return [...currentItems, itemToAdd];
		});
	}

	/**
	 * Updates a single nav item by ID
	 * @param navItemId the ID of the nav item to update
	 * @param updatedNavItem updated nav item which will be merged with the existing nav item
	 */
	public updateSecondaryNavListItem(navItemId: string, updatedNavItem: Partial<SecondaryNavList>): void {
		this.secondaryNavListItems.update((secondaryNavItems) => {
			return secondaryNavItems.map((item) => (item.id === navItemId ? { ...item, ...updatedNavItem } : item));
		});
	}

	/**
	 * Determine if the module has secondary nav
	 * @param hasSecondaryNav - boolean representation of secondary nav toggle.
	 */
	public setHasSecondaryNav(hasSecondaryNav: boolean): void {
		this.hasSecondaryNav.set(hasSecondaryNav);
	}

	/**
	 * Determine if the module has secondary nav button
	 * @param hasSecondaryNavButton - boolean representation of secondary nav button.
	 */
	public setHasSecondaryNavButton(hasSecondaryNavButton: boolean): void {
		this.hasSecondaryNavButton.set(hasSecondaryNavButton);
	}

	/**
	 * set secondary nav button data
	 * @param secNavButtonData - secNavButtonData representation of secondary nav button data.
	 */
	public setSecNavButtonData(secNavButtonData: SecNavButonData): void {
		this.secNavButtonData.set(secNavButtonData);
	}

	/**
	 * Set active list item
	 * @param activeListItem - SecondaryNavList representation of active list item
	 */
	public setActiveListItem(activeListItemId: string): void {
		const activeListItem = this.secondaryNavListItems().find((item) => item.id === activeListItemId);
		if (activeListItem) {
			this.activeListItem$.next(activeListItem);
		}
	}

	/**
	 * Get active list item
	 * @returns activeListItem - SecondaryNavList representation of active list item
	 */
	public getActiveListItem$(): Observable<SecondaryNavList> {
		return this.activeListItem$;
	}

	/**
	 * Get active list item
	 * @returns activeListItem - SecondaryNavList representation of active list item
	 */
	public getActiveListItem(): SecondaryNavList {
		return this.activeListItem$.getValue();
	}

	/**
	 * Set permission flag
	 * @param isPermissionAvailable - string representation of permission flag
	 */
	public setIsPermissionAvailable(isPermissionAvailable: boolean): void {
		this.isPermissionAvailable = isPermissionAvailable;
	}

	/**
	 * Get permission flag
	 * @returns isPermissionAvailable - string representation of permission flag
	 */
	public getIsPermissionAvailable(): boolean {
		return this.isPermissionAvailable;
	}

	public resetSecondaryNavParams(): void {
		this.hasSecondaryNav.set(false);
		this.hasSecondaryNavButton.set(false);
		this.showSecondaryNav.set(true);
	}

	/**
	 * set secondary nav header
	 * @param secNavHeader - secNavHeader representation of secondary nav header data.
	 */
	public setSecNavHeader(secNavHeader: string): void {
		this.secNavHeader.set(secNavHeader);
	}

	/**
	 * Determine if the module has secondary nav toggle button
	 * @param showSecondaryNav - boolean representation of secondary nav toggle button.
	 */
	public setToggleNavButton(showSecondaryNavValue: boolean): void {
		this.showSecondaryNav.set(showSecondaryNavValue);
	}

	/**
	 * This is to check if the active list item is in the url
	 * It is specifically used when user clicks on main nav button or browser back button
	 */
	public checkIfUrlMatchesActiveItem(): boolean {
		const item = this.getActiveListItem();
		if (!item) {
			return false;
		}

		let path = `/${item.feature}/${item.path}`;
		if (item.keyInParams) {
			path = `/${item.feature}/${item.key}/${item.path}`;
		}

		return this.router.url === path;
	}

	/**
	 * This is to retrieve active list item from the url when user reloaded the browser
	 * when user reloads, the active list item in the url should be shown
	 */
	public getActiveListItemFromUrl(
		options: GetActiveNavItemOptions = { ignorePath: false }
	): SecondaryNavList | undefined {
		return this.secondaryNavListItems().find((navItem) => {
			let url = navItem.feature;
			if (navItem.keyInParams) {
				url += `/${navItem.key}`;
			}

			if (!options.ignorePath) {
				url += `/${navItem.path}`;
			}

			return this.router.isActive(url, {
				paths: 'subset',
				fragment: 'ignored',
				queryParams: 'ignored',
				matrixParams: 'ignored'
			});
		});
	}
}
